import React, { useState, useEffect, Fragment, forwardRef, useImperativeHandle } from 'react';
import { PDFDocument, rgb } from 'pdf-lib';
import { Document, Page } from 'react-pdf';
import { toast } from 'react-toastify';
import { css } from 'twin.macro';
import { getDateFromDateTime } from '@/_helpers';
import PDFControls from './PDFControls';
import { FullViewLoadingSpinner } from '@/_elements/Loaders';
import { DEFAULT_FONT_SIZE, DEFAULT_LINE_HEIGHT_FACTOR } from '../smartpdf/constants';
import { getPreviewValueFromBlockInput } from './utils';

const CHECKBOX_JOT_FORM =
    'https://optonome-staging.s3.amazonaws.com/chatroom/62f30a89997e8e0018b16bb9/67989ffd08e3658962397884_1738055677092_checkbox.png';
const RADIO_JOT_FORM =
    'https://optonome-staging.s3.us-east-2.amazonaws.com/chatroom/62f30a89997e8e0018b16bb9/6798a1d708e36589623978e9_1738056151743_radio.png';
const CHECKBOX_ICON_FULL =
    'https://optonome-staging.s3.us-east-2.amazonaws.com/chatroom/62f30a89997e8e0018b16bb9/679323bbbc35d1548c9d1a8e_1737696187807_checkbox_v2.png';
const RADIO_ICON_FULL =
    'https://optonome-staging.s3.us-east-2.amazonaws.com/chatroom/62f30a89997e8e0018b16bb9/679352070f28b29c9ae9d838_1737708039478_radio_v2.png';
let imageBytesMap = {};
let pageDimensionMap = {};

function isValidRenderField(page, field, pageDimension) {
    const isPDFPage = page && typeof page.drawText === 'function';

    const hasValidField =
        field &&
        typeof field.x === 'number' &&
        !isNaN(field.x) &&
        typeof field.y === 'number' &&
        !isNaN(field.y) &&
        typeof field.width === 'number' &&
        !isNaN(field.width) &&
        typeof field.height === 'number' &&
        !isNaN(field.height);

    const hasValidPageDimension =
        pageDimension &&
        typeof pageDimension.width === 'number' &&
        !isNaN(pageDimension.width) &&
        typeof pageDimension.height === 'number' &&
        !isNaN(pageDimension.height);

    return isPDFPage && hasValidField && hasValidPageDimension;
}
const TemplatePreview = forwardRef(
    ({ formTemplateUrl, formElements = [], formName = '', pdfDimension, canDownload }, ref) => {
        const [pdfUrl, setPdfUrl] = useState(null);
        const [pdfBytes, setPdfBytes] = useState(null);
        const [formValues, setFormValues] = useState(null);
        const [imageValues, setImageValues] = useState(null);
        const [numPages, setNumPages] = useState(null);
        const [isBusy, setIsBusy] = useState(true);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null);
        const [pdfScale, setPdfScale] = useState(1);

        const templateFormElements = (formElements || [])?.filter(
            (x) => x?.config?.templateConfig || x?.config?.hasTemplateConfig
        );
        useEffect(() => {
            if (!isBusy) {
                const initialValues = {};
                const imageValues = {};

                templateFormElements.forEach((_field) => {
                    const field = {
                        ...(_field?.config?.templateConfig || {}),
                        ...(_field?.config?.templateConfig?.coordinates || {}),
                        ..._field,
                    };
                    const fieldId = _field?._id || _field?.id;
                    if (field.key === 'Signature') {
                        imageValues[fieldId] = {
                            url: `${field.value}?noCache=${Math.random()}`,
                        };
                    } else if (field.key === 'Header') {
                        initialValues[fieldId] = field.fieldName || '';
                    } else {
                        initialValues[fieldId] = field.value;
                    }
                });

                setFormValues(initialValues);
                setImageValues(imageValues);
            }
        }, [isBusy]);

        useEffect(() => {
            if (formValues && imageValues) {
                setTimeout(() => download(true), 1000);
            }
        }, [formValues, imageValues]);

        useEffect(() => {
            if (formTemplateUrl) {
                fetchPDF(formTemplateUrl);
            }
        }, [formTemplateUrl]);

        useImperativeHandle(ref, () => ({
            download,
        }));

        const fetchPDF = async (url) => {
            try {
                const response = await fetch(url);
                const buffer = await response.arrayBuffer();
                const pdfDoc = await PDFDocument.load(buffer);
                const form = pdfDoc.getForm();
                form.flatten();
                const flattenedPdfBytes = await pdfDoc.save();
                setPdfBytes(flattenedPdfBytes);
            } catch (error) {
                console.error('Error fetching PDF:', error);
                toast.error('Failed to fetch template. Please try again.');
            } finally {
                setIsBusy(false);
            }
        };

        const drawTextInField = async (
            page,
            text,
            field,
            fontSize = DEFAULT_FONT_SIZE,
            lineHeightFactor = DEFAULT_LINE_HEIGHT_FACTOR,
            { pageDimension } = {}
        ) => {
            if (!isValidRenderField(page, field, pageDimension)) {
                return;
            }
            const padding = 0;
            const lineHeight = fontSize * lineHeightFactor;
            const formattedText = text.replaceAll('\r', '');
            let spacesToFitWidth = Math.ceil(field.width / Math.floor(fontSize / 2));
            spacesToFitWidth = spacesToFitWidth > pageDimension.width ? pageDimension.width : spacesToFitWidth;
            const wrapText = (text, maxCharsPerLine) => {
                const words = text.split(' ');
                const lines = [];
                let currentLine = '';

                words.forEach((word) => {
                    if ((currentLine + word).length > maxCharsPerLine) {
                        lines.push(currentLine.trim());
                        currentLine = word + ' ';
                    } else {
                        currentLine += word + ' ';
                    }
                });
                if (currentLine.trim()) {
                    lines.push(currentLine.trim());
                }

                return lines;
            };

            const finalLines = formattedText.split('\n').flatMap((line) => wrapText(line, spacesToFitWidth));

            let currentY = field.y;

            if (finalLines.length === 1) {
                const singleLine = finalLines[0];
                const centeredY = field.y - field.height / 2 + fontSize / 1.5;
                // console.log({
                //     x: field.x + padding,
                //     y: centeredY - fontSize,
                // });
                return page.drawText(singleLine, {
                    x: field.x + padding,
                    y: centeredY - fontSize,
                    size: fontSize,
                    color: rgb(0, 0, 0),
                });
            }

            finalLines.forEach((line) => {
                // if (currentY < field.y - field.height + fontSize) return;
                //rectangular overlay on text in pdf
                // const _x = field.x + padding;
                // const _y = currentY - fontSize - padding;
                // page.drawRectangle({
                //     x: _x,
                //     y: _y,
                //     width: field.width + 2,
                //     height: fontSize,
                //     color: rgb(1, 1, 1),
                //     borderColor: rgb(1, 1, 1),
                // });
                // eslint-disable-next-line no-control-regex
                const safeTextLine = line.replace(/[^\x00-\xFF]/g, '');
                page.drawText(safeTextLine, {
                    x: field.x + padding,
                    y: currentY - fontSize - padding,
                    size: fontSize,
                    color: rgb(0, 0, 0),
                });
                currentY -= lineHeight;
            });
        };

        const drawBlockTextInField = async (
            page,
            text,
            field,
            fontSize = DEFAULT_FONT_SIZE,
            lineHeightFactor = DEFAULT_LINE_HEIGHT_FACTOR,
            { pageDimension } = {}
        ) => {
            if (!isValidRenderField(page, field, pageDimension)) {
                return;
            }
            const padding = 0;
            const lineHeight = fontSize * lineHeightFactor;
            const formattedText = text.replaceAll('\r', '');
            let spacesToFitWidth = Math.ceil(field.width / Math.floor(fontSize / 2));
            spacesToFitWidth = spacesToFitWidth > pageDimension.width ? pageDimension.width : spacesToFitWidth;
            const wrapText = (text, maxCharsPerLine) => {
                const words = text.split(' ');
                const lines = [];
                let currentLine = '';

                words.forEach((word) => {
                    if ((currentLine + word).length > maxCharsPerLine) {
                        lines.push(currentLine.trim());
                        currentLine = word + ' ';
                    } else {
                        currentLine += word + ' ';
                    }
                });
                if (currentLine.trim()) {
                    lines.push(currentLine.trim());
                }

                return lines;
            };

            const finalLines = formattedText.split('\n').flatMap((line) => wrapText(line, spacesToFitWidth));

            let currentY = field.y;

            if (finalLines.length === 1) {
                const singleLine = finalLines[0];
                const centeredY = field.y - field.height / 2 + fontSize / 1.5;

                if (true) {
                    const characterWidth = field.width / text.length;

                    for (let i = 0; i < text.length; i++) {
                        const char = text[i];
                        const charX = field.x + i * characterWidth + characterWidth / 2 - fontSize / 3;

                        page.drawText(char, {
                            x: charX,
                            y: centeredY - fontSize,
                            size: fontSize,
                            color: rgb(0, 0, 0),
                        });
                    }
                    return;
                }
            }

            finalLines.forEach((line) => {
                const safeTextLine = line.replace(/[^\x00-\xFF]/g, '');
                page.drawText(safeTextLine, {
                    x: field.x + padding,
                    y: currentY - fontSize - padding,
                    size: fontSize,
                    color: rgb(0, 0, 0),
                });
                currentY -= lineHeight;
            });
        };

        const toAbsoluteCoordinates = (field, pageDimension) => ({
            x: field.x * pageDimension.width,
            y: (1 - field.y) * pageDimension.height,
            width: field.width * pageDimension.width,
            height: field.height * pageDimension.height,
        });

        const base64ToArrayBuffer = (base64) => Uint8Array.from(atob(base64.split(',')[1]), (c) => c.charCodeAt(0));

        const drawImageInField = async (pdfDoc, page, field, imageUrl, { pageDimension } = {}) => {
            if (!isValidRenderField(page, field, pageDimension)) {
                return;
            }
            const padding = field?.padding || 0;
            const coords = field?.coordinates || field;
            const pdfX = coords.x * pageDimension.width;
            const pdfY = pageDimension.height - coords.y * pageDimension.height - coords.height * pageDimension.height;
            const pdfWidth = coords.width * pageDimension.width;
            const pdfHeight = coords.height * pageDimension.height;
            const availableBoxWidth = pdfWidth;
            const availableBoxHeight = pdfHeight;
            let imageBytes;

            try {
                if (imageBytesMap[imageUrl]) {
                    imageBytes = imageBytesMap[imageUrl];
                } else {
                    if (imageUrl.includes('image/png')) {
                        imageBytes = base64ToArrayBuffer(imageUrl);
                    } else {
                        imageBytes = await fetch(`${imageUrl}?noCache=${Math.random()}`).then((res) =>
                            res.arrayBuffer()
                        );
                    }
                    imageBytesMap[imageUrl] = imageBytes;
                }
                const image =
                    imageUrl.includes('.png') || imageUrl.includes('image/png')
                        ? await pdfDoc.embedPng(imageBytes)
                        : await pdfDoc.embedJpg(imageBytes);

                const { width: imageWidth, height: imageHeight } = image.scale(1);
                const imageAspectRatio = imageWidth / imageHeight;

                let resizedWidth = availableBoxWidth;
                let resizedHeight = availableBoxHeight;
                if (resizedWidth / resizedHeight > imageAspectRatio) {
                    resizedWidth = resizedHeight * imageAspectRatio;
                } else {
                    resizedHeight = resizedWidth / imageAspectRatio;
                }

                const centerX = pdfX + (availableBoxWidth - resizedWidth) / 2;
                const centerY = pdfY + (availableBoxHeight - resizedHeight) / 2;
                page.drawImage(image, {
                    x: centerX + padding / 2,
                    y: centerY + padding,
                    width: resizedWidth - padding * 2,
                    height: resizedHeight - padding * 2,
                });
            } catch (error) {
                console.error('Error embedding image:', error);
            }
        };

        const download = async (preview = false) => {
            try {
                setLoading(true);
                const pdfDoc = await PDFDocument.load(pdfBytes);
                const pages = pdfDoc.getPages();
                pages.forEach((page, index) => {
                    const { width, height } = page.getSize();
                    pageDimensionMap[index + 1] = { width, height };
                });
                for (const _field of templateFormElements) {
                    const fieldId = _field?._id || _field?.id;
                    const field = {
                        ...(_field?.config?.templateConfig || {}),
                        ...(_field?.config?.templateConfig?.coordinates || {}),
                        ..._field,
                    };
                    const fieldKey = field?.key || '';
                    const page = pages[field.page - 1];
                    const pageDimension = pageDimensionMap[field?.page] || pdfDimension;
                    if (fieldKey === 'Signature' && imageValues[fieldId]?.url) {
                        const imageUrl = imageValues[fieldId]?.url || '';
                        await drawImageInField(pdfDoc, page, field, imageUrl, {
                            pageDimension,
                        });
                    } else if (fieldKey === 'Header') {
                        const absField = toAbsoluteCoordinates(field, pageDimension);
                        await drawTextInField(page, formValues[fieldId] || '', absField, undefined, undefined, {
                            pageDimension,
                        });
                        if (field?.options && field?.options?.showTitleInPdfTemplateView) {
                            const absField = toAbsoluteCoordinates(
                                {
                                    ...field,
                                    y: field?.y + field?.height,
                                },
                                pageDimension
                            );
                            await drawTextInField(page, field?.fieldName || '', absField, 20, undefined, {
                                pageDimension,
                            });
                        }
                    } else if (fieldKey === 'InputField') {
                        if (
                            (field?.options || {})?.enableBlockInput &&
                            field?.data &&
                            Array.isArray(field?.data) &&
                            field?.data?.length
                        ) {
                            const previewData = getPreviewValueFromBlockInput({
                                data: field.data,
                                value: formValues[fieldId],
                                key: 'InputField',
                            });
                            const selectedOptionValues = field.data || [];
                            for (let i = 0; i < selectedOptionValues.length; i++) {
                                const option = selectedOptionValues[i];
                                const coordinates =
                                    option?.config?.templateConfig?.coordinates || option?.config?.templateConfig || {};
                                const page = pages[coordinates.page - 1];
                                console.log({ coordinates, pages, page, config: option?.config });
                                const text = (previewData.find((block) => block[option.value])?.[option.value] || '')
                                    .padEnd(option.blockSize, ' ')
                                    .substring(0, option.blockSize);
                                console.log({ coordinates, option });
                                const absField = toAbsoluteCoordinates(coordinates, pageDimension);
                                await drawBlockTextInField(page, text, absField, undefined, undefined, {
                                    pageDimension,
                                });
                            }
                        } else if (field?.config?.templateConfig) {
                            const absField = toAbsoluteCoordinates(field, pageDimension);
                            await drawTextInField(page, formValues[fieldId] || '', absField, undefined, undefined, {
                                pageDimension,
                            });
                        }
                    } else if (fieldKey === 'NumberField') {
                        if (field?.config?.templateConfig) {
                            const absField = toAbsoluteCoordinates(field, pageDimension);
                            await drawTextInField(page, formValues[fieldId] || '', absField, undefined, undefined, {
                                pageDimension,
                            });
                        }
                    } else if (fieldKey === 'Checkbox' || fieldKey === 'Radio') {
                        if (!formValues[fieldId]) {
                            continue;
                        }
                        const isRadio = fieldKey === 'Radio';
                        const selectedOptions = isRadio ? formValues[fieldId] : JSON.parse(formValues[fieldId]);
                        const selectedOptionValues = (field.data || []).filter(
                            (option) => option?.value && (selectedOptions || [])?.includes(option?.value)
                        );
                        for (let i = 0; i < selectedOptionValues.length; i++) {
                            const coordinates =
                                selectedOptionValues[i]?.config?.templateConfig?.coordinates ||
                                selectedOptionValues[i]?.config?.templateConfig ||
                                {};
                            const page = pages[coordinates.page - 1];
                            const pageDimension = pageDimensionMap[coordinates.page] || pdfDimension;
                            await drawImageInField(
                                pdfDoc,
                                page,
                                // { ...field, ...coordinates, coordinates, padding: 1 },
                                { ...field, ...coordinates, coordinates },
                                `${isRadio ? RADIO_JOT_FORM : CHECKBOX_JOT_FORM}`,
                                {
                                    pageDimension,
                                }
                            );
                        }
                    } else if (fieldKey === 'Select') {
                        let text = '';
                        const selectedOption = (field.data || []).find(
                            (option) => option?.value === formValues[fieldId]
                        );
                        if ((field?.options || {})?.enableDataValue) {
                            text = selectedOption?.dataValue;
                        } else {
                            text = selectedOption?.text;
                        }
                        const absField = toAbsoluteCoordinates(field?.coordinates || field, pageDimension);
                        await drawTextInField(page, text || '', absField, undefined, undefined, {
                            pageDimension,
                        });
                    } else if (fieldKey === 'DatePicker') {
                        if (
                            (field?.options || {})?.enableBlockInput &&
                            field?.data &&
                            Array.isArray(field?.data) &&
                            field?.data?.length
                        ) {
                            const previewData = getPreviewValueFromBlockInput({
                                data: field.data,
                                value: getDateFromDateTime(formValues[fieldId]),
                                key: 'DatePicker',
                            });
                            const selectedOptionValues = field.data || [];
                            for (let i = 0; i < selectedOptionValues.length; i++) {
                                const option = selectedOptionValues[i];
                                const coordinates =
                                    option?.config?.templateConfig?.coordinates || option?.config?.templateConfig || {};
                                const page = pages[coordinates.page - 1];
                                const text = (previewData.find((block) => block[option.value])?.[option.value] || '')
                                    .padEnd(option.blockSize, ' ')
                                    .substring(0, option.blockSize);

                                console.log({ coordinates, option });

                                const absField = toAbsoluteCoordinates(coordinates, pageDimension);
                                await drawBlockTextInField(page, text || '', absField, undefined, undefined, {
                                    pageDimension,
                                });
                            }
                        } else if (field?.config?.templateConfig) {
                            const absField = toAbsoluteCoordinates(field, pageDimension);
                            await drawTextInField(
                                page,
                                getDateFromDateTime(formValues[fieldId]) || '',
                                absField,
                                undefined,
                                undefined,
                                {
                                    pageDimension,
                                }
                            );
                        }
                    }
                }

                const blob = new Blob([await pdfDoc.save()], {
                    type: 'application/pdf',
                });
                const url = URL.createObjectURL(blob);
                if (preview) {
                    setPdfUrl(url);
                } else {
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${formName || 'form'}.pdf`;
                    link.click();
                    URL.revokeObjectURL(url);
                }
            } catch (error) {
                const message = 'Error generating Template PDF. Please try again.';
                console.error(message, error);
                toast.error(message);
                setError(message);
            } finally {
                setLoading(false);
            }
        };

        const onDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);

        if (error) {
            return <div className="text-danger text-center mt-4"> {error} </div>;
        }

        return (
            <div
                style={{ backgroundColor: '#3c3c3c66' }}
                className="pb-2 pt-2 d-flex flex-column justify-content-center"
            >
                <div className="pb-2 pt-2 mr-4 d-flex justify-content-end  align-items-center ">
                    <div className="d-flex justify-content-end  align-items-center ">
                        <PDFControls
                            setPdfScale={setPdfScale}
                            isBusy={isBusy || loading}
                            pdfScale={pdfScale}
                            onDownload={() => download()}
                            canDownload={canDownload}
                        />
                    </div>
                </div>

                {pdfUrl && !isBusy ? (
                    <div
                        style={{
                            width: '100%',
                            backgroundColor: '#525659',
                            padding: '10px',
                            overflow: 'auto',
                            maxHeight: '80vh',
                        }}
                    >
                        <Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess}>
                            {Array.from({ length: numPages }, (_, index) => (
                                <div
                                    key={`page-container_${index + 1}`}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        margin: '10px 0',
                                    }}
                                >
                                    <Page key={`page_${index + 1}`} pageNumber={index + 1} scale={pdfScale} />
                                </div>
                            ))}
                        </Document>
                    </div>
                ) : (
                    <FullViewLoadingSpinner />
                )}
            </div>
        );
    }
);

export default TemplatePreview;
